import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { InputSwitch } from "primereact/inputswitch";
import { MultiSelect } from "primereact/multiselect";

import {
  useGetCustomerMeQuery,
  useUpdateCustomerNotificationChannelsMutation,
  useUpdateCustomerAllergiesMutation,
} from "../../../redux/services/customer";
import { allergiesOptions } from "../OrderPage/constants/allergies";
import { ToastContext } from "../../../App";
import { getEffectiveAllergies } from "../../../utils/customerDefaults";

import NotificationIcon from "../../../assets/notification.svg";
import MessageIcon from "../../../assets/message.svg";
import TelegramIcon from "../../../assets/telegram.svg";
import SmsIcon from "../../../assets/sms.svg";

import * as styles from "./Settings.module.css";

const CHANNELS = [
  {
    key: "push",
    icon: NotificationIcon,
    title: "Push Notifications",
    description: "Receive push notifications",
  },
  {
    key: "email",
    icon: MessageIcon,
    title: "Email Notifications",
    description: "Receive updates via email",
  },
  {
    key: "telegram",
    icon: TelegramIcon,
    title: "Telegram Notifications",
    description: "Receive updates via Telegram",
  },
  {
    key: "text",
    icon: SmsIcon,
    title: "SMS Notifications",
    description: "Receive updates via SMS",
  },
];

const Settings = () => {
  const showToast = useContext(ToastContext);

  const { data: profile } = useGetCustomerMeQuery();
  const [updateChannels] = useUpdateCustomerNotificationChannelsMutation();
  const [updateAllergies] = useUpdateCustomerAllergiesMutation();

  const channelsFromProfile = useMemo(
    () => profile?.notification_channels || [],
    [profile],
  );
  const allergiesFromProfile = useMemo(
    () => (profile ? getEffectiveAllergies(profile) : []),
    [profile],
  );

  const [channels, setChannels] = useState(channelsFromProfile);
  const [allergies, setAllergies] = useState(allergiesFromProfile);
  const [allergyPanelHeight, setAllergyPanelHeight] = useState(0);

  useEffect(() => {
    setChannels(channelsFromProfile);
  }, [channelsFromProfile]);

  useEffect(() => {
    setAllergies(allergiesFromProfile);
  }, [allergiesFromProfile]);

  const toggleChannel = async (key, enabled) => {
    if (enabled) {
      const hasContact = (() => {
        switch (key) {
          case "push":
            return true;
          case "email":
            return !!profile?.email;
          case "telegram":
            return !!profile?.telegram_chat_id;
          case "text":
            return !!profile?.phone;
          default:
            return false;
        }
      })();
      if (!hasContact) {
        const label =
          { email: "email", telegram: "Telegram account", text: "phone number" }[
            key
          ] || key;
        showToast?.(
          "error",
          "Notifications",
          `Please add your ${label} in My Account first`,
        );
        return;
      }
    }
    const next = enabled
      ? Array.from(new Set([...channels, key]))
      : channels.filter((c) => c !== key);
    setChannels(next);
    try {
      await updateChannels({ channels: next }).unwrap();
      showToast?.("success", "Notifications", "Notification settings updated");
    } catch (e) {
      setChannels(channels);
      showToast?.(
        "error",
        "Notifications",
        e?.data?.message || "Failed to update notification channels",
      );
    }
  };

  const handleAllergyChange = async (e) => {
    let next = Array.isArray(e.value) ? e.value : [];
    const justAdded = e.selectedOption?.value;

    if (justAdded === "none") {
      next = ["none"];
    } else if (
      justAdded &&
      justAdded !== "none" &&
      allergies.length === 1 &&
      allergies[0] === "none"
    ) {
      next = [justAdded];
    } else if (next.includes("none") && next.length > 1) {
      next = next.filter((v) => v !== "none");
    }

    setAllergies(next);
    try {
      await updateAllergies({ allergies: next }).unwrap();
      showToast?.("success", "Allergies", "Allergies updated");
    } catch (err) {
      setAllergies(allergies);
      showToast?.(
        "error",
        "Allergies",
        err?.data?.message || "Failed to update allergies",
      );
    }
  };

  const dropdownIcon = (
    <svg
      width="30"
      height="12"
      viewBox="0 0 30 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M1 1L15 11L29 1"
        stroke="rgba(164, 164, 164, 1)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const removeIcon = (options) => (
    <svg
      {...(options?.iconProps || {})}
      width="11"
      height="11"
      viewBox="0 0 11 11"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: "block" }}
    >
      <path
        d="M1 1L10 10M10 1L1 10"
        stroke="rgba(48, 48, 48, 1)"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );

  const allergiesWrapperRef = useRef(null);

  const onAllergyShow = () => {
    requestAnimationFrame(() => {
      const panel = allergiesWrapperRef.current?.querySelector(
        ".p-multiselect-panel",
      );
      if (panel) setAllergyPanelHeight(panel.offsetHeight + 8);
    });
  };

  const onAllergyHide = () => setAllergyPanelHeight(0);

  const itemTemplate = (option) => {
    const selected = allergies.includes(option.value);
    return (
      <div className={styles.allergyItem}>
        <span
          className={styles.allergyItemLabel}
          style={selected ? { color: "rgba(255, 255, 255, 1)" } : undefined}
        >
          {option.label}
        </span>
        {selected && (
          <svg
            className={styles.allergyItemCheck}
            width="28"
            height="20"
            viewBox="0 0 28 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M2 10.5L10.5 18.5L26 2"
              stroke="rgba(255, 255, 255, 1)"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>
    );
  };

  return (
    <div className={styles.pageWrapper}>
      <h1 className={styles.title}>Settings</h1>
      <p className={styles.description}>
        Manage your preferences and account settings
      </p>

      <h2 className={styles.sectionTitle}>Notifications</h2>
      <div className={styles.card}>
        {CHANNELS.map(({ key, icon, title, description }) => (
          <div key={key} className={styles.subCard}>
            <div className={styles.iconBox}>
              <img src={icon} alt="" />
            </div>
            <div className={styles.subCardText}>
              <span className={styles.subCardTitle}>{title}</span>
              <span className={styles.subCardDescription}>{description}</span>
            </div>
            <div className={styles.switch}>
              <InputSwitch
                checked={channels.includes(key)}
                onChange={(e) => toggleChannel(key, e.value)}
              />
            </div>
          </div>
        ))}
      </div>

      <div className={styles.sectionGap} />

      <h2 className={styles.sectionTitle}>any allergies?</h2>
      <div className={styles.allergiesWrapper} ref={allergiesWrapperRef}>
        <MultiSelect
          value={allergies}
          options={allergiesOptions}
          optionLabel="label"
          optionValue="value"
          onChange={handleAllergyChange}
          display="chip"
          placeholder="Select your allergies"
          panelClassName={styles.allergiesPanel}
          dropdownIcon={dropdownIcon}
          removeIcon={removeIcon}
          itemTemplate={itemTemplate}
          appendTo="self"
          onShow={onAllergyShow}
          onHide={onAllergyHide}
        />
      </div>
      {allergyPanelHeight > 0 && <div style={{ height: allergyPanelHeight }} />}
    </div>
  );
};

export default Settings;
